import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PageBlocks from "@/components/PageBlocks";

export function ServicePageShell({
  contentKey,
  defaultContent = {},
  missions = [],
  sections = [],
  actionButtons = [],
  news = [],
}) {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    if (!contentKey) return;
    api
      .get(`/content/${contentKey}`)
      .then((r) => {
        const data = r.data || {};
        setContent({ ...defaultContent, ...data });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey]);

  const eyebrow = content.eyebrow || "Service";
  const title = content.title || "Service UNISIC";
  const bannerImage = content.banner_image || content.bannerImage;
  const intro = content.intro || "";

  return (
    <div>
      <section className="relative h-[46vh] min-h-[340px] w-full overflow-hidden">
        {bannerImage && (
          <img src={bannerImage} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight max-w-4xl">
            {title}
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow eyebrow-dark">Présentation</div>
            <span className="divider-gold mt-3" />
            <p className="mt-6 text-lg text-slate-700 leading-relaxed whitespace-pre-line">
              {intro}
            </p>

            {content.body && (
              <div
                className="mt-8 text-slate-700 leading-relaxed whitespace-pre-line space-y-4 text-[15px]"
                data-testid="service-body"
              >
                {content.body.split(/\n{2,}/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}

            <PageBlocks
              blocks={content.blocks}
              legacyBody={!content.body ? content.legacy_body : ""}
              className="mt-8"
            />

            {sections.map((s, i) => (
              <div key={i} className="mt-12">
                <h2 className="text-2xl font-bold text-unisic-blue">{s.title}</h2>
                <span className="divider-gold mt-3" />
                <div className="mt-4 text-slate-700 leading-relaxed">{s.content}</div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-4 space-y-6">
            {missions.length > 0 && (
              <div className="paper-card p-6">
                <div className="eyebrow eyebrow-dark">Nos missions</div>
                <ul className="mt-4 space-y-3 text-sm text-slate-700">
                  {missions.map((m, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-2 w-1.5 h-1.5 bg-unisic-gold rounded-full flex-shrink-0" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {actionButtons.length > 0 && (
              <div
                className="rounded-sm p-6"
                style={{ background: "#0A2E52", color: "#ffffff" }}
              >
                <div
                  className="text-[11px] font-bold uppercase tracking-[0.28em]"
                  style={{ color: "#E5C158" }}
                >
                  Accès rapide
                </div>
                <p className="mt-2 text-xs text-white/70 leading-relaxed">
                  Passez directement à la plateforme numérique correspondante.
                </p>
                <div className="mt-5 space-y-3">
                  {actionButtons.map((b, i) => (
                    <Link
                      key={i}
                      to={b.to}
                      className="flex items-center justify-between gap-3 px-4 py-3 border border-white/30 hover:border-unisic-gold hover:bg-white/10 rounded-sm text-sm font-medium text-white transition-colors"
                      data-testid={`service-action-${i}`}
                    >
                      <span>{b.label}</span>
                      <ArrowRight className="w-4 h-4 text-unisic-gold flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {news.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
            <div className="eyebrow eyebrow-dark">Actualités du service</div>
            <h2 className="mt-3 text-2xl font-bold text-unisic-blue">Dernières informations</h2>
            <span className="divider-gold mt-3" />
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {news.map((n) => (
                <div key={n.id} className="paper-card p-5">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">{n.category}</div>
                  <h3 className="mt-1 font-display font-semibold text-unisic-blue">{n.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3">{n.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
